/**
 * VehicleAttachments Component
 * Visar och hanterar bilagor kopplade till ett fordon
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '@/store/languageStore';
import apiClient from '@/services/api/client';
import { apiUrl } from '@/utils/apiAssetUrl';
import {
  ATTACHMENT_CATEGORY_OPTIONS,
  VehicleAttachmentCategoryField,
  attachmentCategoryColor,
  attachmentCategoryLabel,
  useVehicleAttachmentCategories,
} from './VehicleAttachmentCategoryField';
import {
  DocumentIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';

interface Attachment {
  id: number;
  vehicle_id: number;
  filename: string;
  original_filename: string;
  file_type: string | null;
  file_size: number | null;
  storage_path: string;
  category: string;
  category_name?: string | null;
  description: string | null;
  uploader_name?: string;
  created_at: string;
  download_url?: string;
}

interface VehicleAttachmentsProps {
  vehicleId: number;
}

const getFileIcon = (fileType: string | null): React.ReactNode => {
  if (!fileType) return <DocumentIcon className="w-8 h-8 text-textTertiary" />;
  
  if (fileType.startsWith('image/')) {
    return <PhotoIcon className="w-8 h-8 text-blue-500" />;
  }
  if (fileType === 'application/pdf') {
    return <DocumentTextIcon className="w-8 h-8 text-red-500" />;
  }
  if (fileType.includes('spreadsheet') || fileType.includes('excel')) {
    return <DocumentChartBarIcon className="w-8 h-8 text-green-500" />;
  }
  return <DocumentIcon className="w-8 h-8 text-textTertiary" />;
};

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const VehicleAttachments: React.FC<VehicleAttachmentsProps> = ({ vehicleId }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { categories: customCategories, addCategory } = useVehicleAttachmentCategories(vehicleId);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadDescription, setUploadDescription] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Preview state
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  /** Miniatyr misslyckades (t.ex. ogiltig path) – visa ikon i stället */
  const [thumbLoadFailed, setThumbLoadFailed] = useState<Record<number, true>>({});

  // Ladda bilagor
  const loadAttachments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/api/vehicles/${vehicleId}/attachments`);
      if (response.data.success) {
        setAttachments(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Error loading attachments:', err);
      setError('Kunde inte ladda bilagor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttachments();
  }, [vehicleId]);

  // Hantera filval
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setShowUploadForm(true);
      setUploadError(null);
    }
  };

  // Ladda upp fil
  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', uploadCategory);
      if (uploadDescription) {
        formData.append('description', uploadDescription);
      }

      const response = await apiClient.post(
        `/api/vehicles/${vehicleId}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        setShowUploadForm(false);
        setSelectedFile(null);
        setUploadCategory('other');
        setUploadDescription('');
        loadAttachments();
      } else {
        setUploadError(response.data.error || 'Uppladdning misslyckades');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setUploadError(err.response?.data?.error || 'Kunde inte ladda upp fil');
    } finally {
      setUploading(false);
    }
  };

  // Avbryt uppladdning
  const cancelUpload = () => {
    setShowUploadForm(false);
    setSelectedFile(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Ta bort bilaga
  const handleDelete = async (attachment: Attachment) => {
    if (!window.confirm(`Vill du ta bort "${attachment.original_filename}"?`)) {
      return;
    }

    try {
      setDeletingId(attachment.id);
      const response = await apiClient.delete(
        `/api/vehicles/${vehicleId}/attachments/${attachment.id}`
      );

      if (response.data.success) {
        loadAttachments();
      }
    } catch (err: any) {
      console.error('Delete error:', err);
      alert('Kunde inte ta bort bilaga');
    } finally {
      setDeletingId(null);
    }
  };

  // Ladda ner bilaga
  const handleDownload = async (attachment: Attachment) => {
    try {
      const response = await apiClient.get(
        `/api/vehicles/${vehicleId}/attachments/${attachment.id}/download`
      );

      if (response.data.success && response.data.data?.url) {
        // Öppna presigned URL i nytt fönster
        window.open(response.data.data.url, '_blank');
      }
    } catch (err: any) {
      console.error('Download error:', err);
      alert('Kunde inte ladda ner fil');
    }
  };

  const canPreview = (fileType: string | null): boolean => {
    if (!fileType) return false;
    if (fileType === 'application/pdf') return true;
    if (fileType.startsWith('image/')) return true;
    return false;
  };

  // Förhandsgranskning – strömning via API (samma origin + cookie), utan presigned MinIO-URL
  const handlePreview = (attachment: Attachment) => {
    if (!canPreview(attachment.file_type)) return;
    setPreviewAttachment(attachment);
    setPreviewUrl(apiUrl(`/api/vehicles/${vehicleId}/attachments/${attachment.id}/file`));
    setPreviewLoading(false);
  };

  const closePreview = () => {
    setPreviewAttachment(null);
    setPreviewUrl(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header med uppladdningsknapp */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {t('vehicleAttachments.attachments')} ({attachments.length})
        </h3>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          <CloudArrowUpIcon className="w-5 h-5" />
          {t('vehicleAttachments.uploadButton')}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar"
        />
      </div>

      {/* Uppladdningsformulär */}
      {showUploadForm && selectedFile && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {getFileIcon(selectedFile.type)}
              <div>
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-textTertiary">
                  {formatFileSize(selectedFile.size)}
                </p>
              </div>
            </div>
            <button
              onClick={cancelUpload}
              className="p-1 hover:bg-blue-100 rounded"
            >
              <XMarkIcon className="w-5 h-5 text-textTertiary" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <VehicleAttachmentCategoryField
              vehicleId={vehicleId}
              value={uploadCategory}
              categories={customCategories}
              onChange={setUploadCategory}
              onCreated={addCategory}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('service.attachments.descriptionOptional')}
              </label>
              <input
                type="text"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder={t('service.attachments.descriptionPlaceholder')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {uploadError && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
              {uploadError}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={cancelUpload}
              className="px-4 py-2 text-textDefault border border-border rounded-lg bg-bgLight hover:bg-bgElevated"
              disabled={uploading}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('service.attachments.uploading')}
                </>
              ) : (
                <>
                  <CloudArrowUpIcon className="w-5 h-5" />
                  {t('service.attachments.upload')}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Filter */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 pb-2 border-b">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              categoryFilter === 'all'
                ? 'bg-primary text-white'
                : 'bg-bgLight text-textDefault hover:bg-bgElevated'
            }`}
          >
            {t('service.attachments.all')} ({attachments.length})
          </button>
          {ATTACHMENT_CATEGORY_OPTIONS.map((cat) => {
            const count = attachments.filter((a) => a.category === cat.value).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.value}
                onClick={() => setCategoryFilter(cat.value)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  categoryFilter === cat.value
                    ? 'bg-primary text-white'
                    : `${attachmentCategoryColor(cat.value)} hover:opacity-80`
                }`}
              >
                {t(cat.labelKey)} ({count})
              </button>
            );
          })}
          {customCategories.map((cat) => {
            const count = attachments.filter((a) => a.category === cat.code).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.code}
                onClick={() => setCategoryFilter(cat.code)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  categoryFilter === cat.code
                    ? 'bg-primary text-white'
                    : `${attachmentCategoryColor(cat.code)} hover:opacity-80`
                }`}
              >
                {cat.name} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Lista med bilagor */}
      {attachments.length === 0 ? (
        <div className="text-center py-12 text-textSecondary">
          <DocumentIcon className="w-12 h-12 mx-auto mb-3 text-textTertiary" />
          <p>{t('service.attachments.noAttachments')}</p>
          <p className="text-sm">{t('service.attachments.uploadHint')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {attachments
            .filter((a) => categoryFilter === 'all' || a.category === categoryFilter)
            .map((attachment) => (
            <div
              key={attachment.id}
              className={`flex items-center justify-between p-3 bg-bgContainer border border-border rounded-lg transition-shadow ${
                canPreview(attachment.file_type)
                  ? 'cursor-pointer hover:shadow-md hover:ring-1 hover:ring-primary/30'
                  : ''
              }`}
              onClick={() => canPreview(attachment.file_type) && handlePreview(attachment)}
              role={canPreview(attachment.file_type) ? 'button' : undefined}
              title={canPreview(attachment.file_type) ? t('vehicleAttachments.clickToPreview') : undefined}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {attachment.file_type?.startsWith('image/') && !thumbLoadFailed[attachment.id] ? (
                  <img
                    src={apiUrl(`/api/vehicles/${vehicleId}/attachments/${attachment.id}/file`)}
                    alt=""
                    className="w-12 h-12 rounded object-cover border border-gray-200 flex-shrink-0 bg-gray-50"
                    onError={() => setThumbLoadFailed((f) => ({ ...f, [attachment.id]: true }))}
                  />
                ) : (
                  getFileIcon(attachment.file_type)
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {attachment.original_filename}
                  </p>
                  <div className="flex items-center gap-3 text-sm text-textTertiary">
                    <span>{formatFileSize(attachment.file_size)}</span>
                    <span>•</span>
                    <span>{formatDate(attachment.created_at)}</span>
                    {attachment.uploader_name && (
                      <>
                        <span>•</span>
                        <span>{attachment.uploader_name}</span>
                      </>
                    )}
                  </div>
                  {attachment.description && (
                    <p className="text-sm text-textSecondary mt-1">
                      {attachment.description}
                    </p>
                  )}
                </div>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${attachmentCategoryColor(
                    attachment.category
                  )}`}
                >
                  {attachmentCategoryLabel(attachment.category, t, customCategories, attachment.category_name)}
                </span>
              </div>

              <div className="flex items-center gap-1 ml-4" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleDownload(attachment)}
                  className="p-2 text-textTertiary hover:text-primary hover:bg-gray-100 rounded-lg"
                  title={t('vehicleAttachments.download')}
                >
                  <ArrowDownTrayIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(attachment)}
                  disabled={deletingId === attachment.id}
                  className="p-2 text-textTertiary hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-50"
                  title={t('vehicleAttachments.deleteFile')}
                >
                  {deletingId === attachment.id ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                  ) : (
                    <TrashIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          ))}

      {/* Förhandsgranskningsmodal – dokument vid ca 50 % storlek */}
      {previewAttachment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={closePreview}
          role="dialog"
          aria-modal="true"
          aria-label={t('vehicleAttachments.preview')}
        >
          <div
            className="bg-white rounded-lg shadow-xl flex flex-col w-[50vw] max-w-[56rem] max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50 shrink-0">
              <p className="font-medium text-gray-900 truncate pr-4">
                {previewAttachment.original_filename}
              </p>
              <button
                type="button"
                onClick={closePreview}
                className="p-2 text-textTertiary hover:text-gray-700 hover:bg-gray-200 rounded-lg shrink-0"
                aria-label={t('vehicleAttachments.closePreview')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-4 flex items-start justify-center bg-gray-100">
              {previewLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                </div>
              ) : previewUrl && previewAttachment.file_type === 'application/pdf' ? (
                <iframe
                  src={previewUrl}
                  title={previewAttachment.original_filename}
                  className="w-full border-0 rounded bg-white shadow h-[70vh] max-h-[800px]"
                />
              ) : previewUrl && previewAttachment.file_type?.startsWith('image/') ? (
                <img
                  src={previewUrl}
                  alt={previewAttachment.original_filename}
                  className="max-w-full h-auto rounded shadow object-contain max-h-[70vh] w-auto"
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export default VehicleAttachments;
